import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import DatePicker from "react-datepicker";
import { format, parseISO, isWithinInterval } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import {
  fetchCompletedQueues,
  selectCompletedQueues,
  selectQueueLoading
} from "../../../../ReduxSlices/Patients/patientQueueSlice";
import LoadingDots from "../../../shared/LoadingDots";

// ✅ Styled date input component
const CustomInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <input
    ref={ref}
    value={value ? format(new Date(value), "do MMM, yyyy") : ""}
    onClick={onClick}
    readOnly
    placeholder={placeholder}
    className="date-picker-input"
    style={{
      marginLeft: "10px",
      backgroundColor: "#E9F7EE",
      border: "1px solid #A5D6A7",
      borderRadius: "8px",
      padding: "0 4px",
      height: "30px",
      fontSize: "13px",
      cursor: "pointer",
    }}
  />
));

const PatientVisitsReport = () => {
  const dispatch = useDispatch();
  const completedQueues = useSelector(selectCompletedQueues);
  const loading = useSelector(selectQueueLoading);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Fetch once (only if store empty)
 useEffect(() => {
  if (completedQueues.length === 0) {
    dispatch(fetchCompletedQueues());
  }
}, [dispatch, completedQueues.length]);

  //  Filter queue data by selected date range
  const filteredQueue = useMemo(() => {
    if (!startDate || !endDate) return completedQueues;
    return completedQueues.filter((entry) => {
      if (!entry.created_at) return false;
      const createdAt = parseISO(entry.created_at);
      return isWithinInterval(createdAt, {
        start: new Date(startDate),
        end: new Date(endDate),
      });
    });
  }, [completedQueues, startDate, endDate]);

  // ✅ Aggregate patients by weekday
  const chartData = useMemo(() => {
    const counts = {
      Sunday: 0,
      Monday: 0,
      Tuesday: 0,
      Wednesday: 0,
      Thursday: 0,
      Friday: 0,
      Saturday: 0,
    };

    filteredQueue.forEach((entry) => {
      if (!entry.created_at) return;
      const day = format(parseISO(entry.created_at), "EEEE");
      counts[day] = (counts[day] || 0) + 1;
    });

    return Object.keys(counts).map((day) => ({
      day,
      patients: counts[day],
    }));
  }, [filteredQueue]);

  console.log("Completed Queues:", completedQueues);
console.log("Filtered Queue:", filteredQueue);
console.log("Chart Data:", chartData);

  return (
    <div className="p-4 bg-white border rounded shadow-md">
      <h2 className="text-2xl font-bold mb-4 text-center">
        Patient Visits by Day of the Week
      </h2>

      {/* ✅ Date Range Filter */}
      <div className="flex items-center justify-center gap-4 mb-6">
        <div className="flex items-center">
          <label className="font-semibold text-sm">Start: </label>
          <DatePicker
            selected={startDate ? new Date(startDate) : null}
            onChange={(date) =>
              setStartDate(date ? date.toISOString().slice(0, 10) : "")
            }
            selectsStart
            startDate={startDate ? new Date(startDate) : null}
            endDate={endDate ? new Date(endDate) : null}
            customInput={<CustomInput placeholder="Start date" />}
            placeholderText="Start date"
          />
        </div>

        <div className="flex items-center ml-[30px]">
          <label className="font-semibold text-sm">End: </label>
          <DatePicker
            selected={endDate ? new Date(endDate) : null}
            onChange={(date) =>
              setEndDate(date ? date.toISOString().slice(0, 10) : "")
            }
            selectsEnd
            startDate={startDate ? new Date(startDate) : null}
            endDate={endDate ? new Date(endDate) : null}
            minDate={startDate ? new Date(startDate) : null}
            customInput={<CustomInput placeholder="End date" />}
            placeholderText="End date"
          />
        </div>
      </div>

      {/* ✅ Bar Chart */}
      {loading ? (
        <LoadingDots />
      ) : chartData.length === 0 ? (
        <p className="text-center text-gray-500">
          No patient visits recorded for the selected range.
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" />
            <YAxis allowDecimals={false} />
            <Tooltip formatter={(value) => [`${value} patients`, "Visits"]} />
            <Legend />
            <Bar
              dataKey="patients"
              fill="#38A169"
              barSize={40}
              name="Patient Visits"
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default PatientVisitsReport;
